import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { ToastType } from '@components/Toast';
import { FuelSurchargeData } from '@store/customers/customerDetails/fuelSurcharge';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { handleToast } from '@utils/utils';

import { setFuelSurchargeStore } from './store';

interface ApiErrorResponse {
  response: {
    data: {
      Messages?: unknown;
    };
  };
}

interface ErrorWithMessage {
  errorMessage: string;
}

type ErrorArray = Array<string | ErrorWithMessage>;

function isApiErrorResponse(err: unknown): err is ApiErrorResponse {
  return (
    err !== null &&
    typeof err === 'object' &&
    'response' in err &&
    err.response !== null &&
    typeof err.response === 'object' &&
    'data' in err.response &&
    err.response.data !== null &&
    typeof err.response.data === 'object' &&
    'Messages' in err.response.data
  );
}

function isErrorArray(err: unknown): err is ErrorArray {
  return (
    err !== null &&
    typeof err === 'object' &&
    'map' in err &&
    typeof (err as { map: unknown }).map === 'function'
  );
}

function isErrorWithMessage(value: unknown): value is ErrorWithMessage {
  return (
    value !== null &&
    typeof value === 'object' &&
    'errorMessage' in value &&
    typeof (value as ErrorWithMessage).errorMessage === 'string'
  );
}

export const getFuelSurchargeCall = async (
  ownerId: number,
  includeDeleted: boolean,
  ownerType: string,
  cb?: () => void,
) => {
  try {
    const resp: V4ApiResponse<FuelSurchargeData> = await apiClient.get(
      `/FuelSurcharge/getfuelsurcharge?ownerId=${ownerId}&ownerType=${ownerType}&includeDeleted=${includeDeleted}`,
    );

    if (resp.isSuccess) return resp.value;

    throw new Error('Failed to fetch fuel surcharge data');
  } catch (err) {
    handleToast(ToastType.Error, 'Failed to fetch fuel surcharge data');
  } finally {
    cb && cb();
  }
};

export const deleteFuelSurchargeCall = async (
  ownerId: number,
  ownerType: string,
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .customerDetails.fuelSurchargeDelete,
) => {
  try {
    const resp: V4ApiResponse<FuelSurchargeData> = await apiClient.post(
      `FuelSurcharge/deletefuelsurcharge?ownerId=${ownerId}&ownerType=${ownerType}`,
      undefined,
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    return resp.value;
  } catch (err) {
    handleToast(ToastType.Error, 'Failed to delete fuel surcharge data');
  }
};

export const saveFuelSurchargeCall = async (
  ownerId: number,
  model: FuelSurchargeData,
  ownerType: string = '0',
  changeTrackingSource: ChangeTrackingSource = CHANGE_TRACKING_SOURCES
    .customerDetails.fuelSurchargeSave,
) => {
  try {
    const resp: V4ApiResponse<FuelSurchargeData> = await apiClient.post(
      'FuelSurcharge/SaveFuelSurcharge',
      {
        model: model,
        ownerId,
        ownerType,
      },
      buildChangeTrackingRequestConfig([changeTrackingSource]),
    );
    setFuelSurchargeStore('errors', []);
    handleToast(ToastType.Success, 'Fuel surcharge data saved successfully');
    return resp.value as unknown as FuelSurchargeData;
  } catch (err: unknown) {
    if (isApiErrorResponse(err)) {
      const messages = err.response.data.Messages || [];
      setFuelSurchargeStore(
        'errors',
        Array.isArray(messages) ? (messages as string[]) : [],
      );
    } else if (isErrorArray(err)) {
      setFuelSurchargeStore(
        'errors',
        err.map((v) => {
          if (typeof v === 'string') {
            return v;
          } else if (isErrorWithMessage(v)) {
            return v.errorMessage;
          }

          return 'Unspecified/unknown error';
        }),
      );
    }
    handleToast(ToastType.Error, 'Failed to save fuel surcharge data');
  }
};
